<template>
    <div ref="chart" style="width: 100%; height: 500px"></div>
</template>
<script>
import * as echarts from "echarts";
import { ref, onMounted, computed, onBeforeUnmount } from "vue";
import { getItem } from "@/store/persistantStorage";
import { formatImpressions } from "@/helpers/graphs";
import { dateFormatAsText, numberFormatImpressions, numberFormatWithSpace } from "@/helpers/data";

export default {
    name: "PublishersImpressionsStackedBarChart",
    props: {
        earningsCsvData: {
            type: Array,
            required: true,
        },
    },
    setup(props) {
        const chart = ref(null);
        let myChart = null;
        const isLightTheme = computed(() => getItem("theme") === "light");
        const selectedPublishers = ref([]);

        const getPublishers = computed(() => {
            return [...new Set(props.earningsCsvData.map((item) => item.publisher))].sort();
        });
        const getDates = computed(() => {
            const allDates = props.earningsCsvData.map((item) => item.date);
            return [...new Set(allDates)].sort((a, b) => new Date(a) - new Date(b));
        });
        const transformData = computed(() => {
            const publishers = getPublishers.value;
            const dates = getDates.value;
            const groupedData = publishers.reduce((acc, publisher) => {
                acc[publisher] = props.earningsCsvData
                    .filter((item) => item.publisher === publisher)
                    .reduce((map, item) => {
                        map[item.date] = item.impressions;
                        return map;
                    }, {});
                return acc;
            }, {});
            const series = publishers.map((publisher) => {
                const isSelectedOrShowAll =
                    selectedPublishers.value.length === 0 || selectedPublishers.value.includes(publisher);
                const publisherDataMap = groupedData[publisher] || {};
                const seriesData = dates.map((date) => {
                    if (!isSelectedOrShowAll) return 0;
                    return publisherDataMap[date] !== undefined ? publisherDataMap[date] : 0;
                });
                return {
                    name: publisher,
                    type: "bar",
                    stack: "Total",
                    data: seriesData,
                    itemStyle: {
                        borderWidth: 0,
                        borderColor: "#ffffff",
                    },
                    label: {
                        show: dates.length < 60,
                        position: "inside",
                        formatter: formatImpressions,
                        color: isLightTheme.value ? "#000000" : "#ffffff",
                    },
                };
            });
            return series.filter((serie) => serie.data.length > 0);
        });

        const legendSelected = computed(() => {
            const selected = {};
            getPublishers.value.forEach((publisher) => {
                selected[publisher] =
                    selectedPublishers.value.length === 0 || selectedPublishers.value.includes(publisher);
            });
            return selected;
        });

        const togglePublisherFilter = (publisherName) => {
            const currentIndex = selectedPublishers.value.indexOf(publisherName);
            if (currentIndex > -1) {
                selectedPublishers.value.splice(currentIndex, 1);
            } else {
                selectedPublishers.value.push(publisherName);
            }

            if (myChart) {
                myChart.setOption({
                    series: transformData.value,
                    legend: {
                        selected: legendSelected.value,
                    },
                });
            }
        };

        onMounted(() => {
            myChart = echarts.init(chart.value);
            const option = {
                title: {
                    text: "Impressions",
                    textStyle: {
                        color: isLightTheme.value ? "#000000" : "#ffffff",
                    },
                },
                tooltip: {
                    trigger: "axis",
                    formatter: function (params) {
                        let result = "<div>" + dateFormatAsText(params[0].name) + "</div>";
                        let totalValue = 0;
                        params.forEach((item) => {
                            totalValue += item.value;
                        });
                        result += `<div style="padding-bottom: 0.25em;">Total: ${numberFormatWithSpace(
                            totalValue
                        )}</div>`;
                        params.forEach((item) => {
                            result +=
                                item.marker + item.seriesName + ": " + numberFormatWithSpace(item.value) + "<br/>";
                        });
                        return result;
                    },
                },
                legend: {
                    data: getPublishers.value,
                    selected: legendSelected.value,
                    ...(getPublishers.value.length >= 7
                        ? {
                              right: "1%",
                              top: "8.5%",
                              orient: "vertical",
                              itemGap: 10,
                          }
                        : {
                              bottom: 0,
                              itemGap: 25,
                          }),
                    type: getPublishers.value.length > 10 ? "scroll" : "plain",
                    textStyle: {
                        color: isLightTheme.value ? "#000000" : "#ffffff",
                        fontSize: getPublishers.value.length >= 7 ? 14 : 15,
                        fontFamily: "Open Sans",
                    },
                },
                grid: {
                    left: "1%",
                    right: getPublishers.value.length >= 7 ? "17%" : "1%",
                    top: "10%",
                    bottom: getPublishers.value.length >= 7 ? "5%" : "10%",
                    containLabel: true,
                },
                xAxis: {
                    type: "category",
                    boundaryGap: true,
                    data: getDates.value,
                    axisLabel: {
                        formatter: dateFormatAsText,
                        color: isLightTheme.value ? "#000000" : "#ffffff",
                    },
                },
                yAxis: {
                    type: "value",
                    axisLine: {
                        lineStyle: {
                            color: isLightTheme.value ? "#000000" : "#ffffff",
                        },
                    },
                    axisLabel: {
                        formatter: numberFormatImpressions,
                        color: isLightTheme.value ? "#000000" : "#ffffff",
                    },
                },
                series: transformData.value,
            };
            myChart.setOption(option);

            myChart.on("legendselectchanged", (params) => {
                togglePublisherFilter(params.name);
            });
        });
        onBeforeUnmount(() => {
            if (myChart) {
                myChart.dispose();
            }
        });
        return {
            chart,
        };
    },
};
</script>
<style scoped></style>
